import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import {ReplaySubject} from 'rxjs';

import {takeUntil} from 'rxjs/operators';

import {
  CommissionTable, CommissionTableListItem
} from '../../interface/commission-table';

@Component({
  selector: 'app-dict-commission-tables',
  templateUrl: './dict-commission-tables.component.html',
  styleUrls: ['./dict-commission-tables.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(
        () => DictCommissionTablesComponent
      )
    }
  ]
})
export class DictCommissionTablesComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() label = 'Tabela prowizyjna';
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() disable = false;
  @Input() companyId?: number;
  @Input() contractTypeId?: number;
  /**
   * Lista przekazywana z rodzica.
   *
   * Dzięki temu 10 selectorów w tabeli
   * nie wykonuje 10 requestów HTTP.
   */
  @Input() commissionTables: CommissionTableListItem[] = [];
  @Output() selectedCommissionTable = new EventEmitter<CommissionTableListItem>();

  items: CommissionTableListItem[] = [];

  filteredItems: CommissionTableListItem[] = [];

  searchControl: FormControl;

  externalControl = true;

  value?: string = null;

  private allItems: CommissionTableListItem[] = [];

  private destroyed$:
    ReplaySubject<boolean>
    = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
  ) {
    this.searchControl =
      this.fb.control('');
  }

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl({
        value: null,
        disabled: this.disable
      });

      this.externalControl = false;
    } else if (this.disable) {
      this.control.disable({
        emitEvent: false
      });
    }

    this.initializeItems();

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (value: string) => {
          this.filterItems(value);
        }
      );
  }

  ngOnChanges(
    changes: SimpleChanges
  ): void {

    if (changes.commissionTables) {
      this.initializeItems();
    }

    if (
      changes.companyId
      || changes.contractTypeId
    ) {
      this.applyAssignmentFilter();

      this.filterItems(
        this.searchControl?.value
      );
    }

    if (
      changes.disable
      && this.control
    ) {
      if (this.disable) {
        this.control.disable({
          emitEvent: false
        });
      } else {
        this.control.enable({
          emitEvent: false
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private initializeItems(): void {
    this.allItems = [
      ...(this.commissionTables || [])
    ].sort(
      (
        a: CommissionTableListItem,
        b: CommissionTableListItem
      ) =>
        (b.validFrom || '')
          .localeCompare(
            a.validFrom || ''
          )
    );

    this.applyAssignmentFilter();

    this.filterItems(
      this.searchControl?.value
    );
  }

  private applyAssignmentFilter(): void {
    if (
      !this.companyId
      || !this.contractTypeId
    ) {
      this.items = [
        ...this.allItems
      ];

      return;
    }

    const currentUuid =
      this.control?.value;

    this.items =
      this.allItems.filter(
        (
          commissionTable:
          CommissionTableListItem
        ) => {

          /*
           * Aktualnie przypisany standard
           * musi pozostać widoczny.
           */
          if (
            commissionTable.uuid
            === currentUuid
          ) {
            return true;
          }

          return (
            commissionTable.assignments
            || []
          ).some(
            assignment =>
              assignment.companyId
              === this.companyId
              &&
              assignment.contractTypeId
              === this.contractTypeId
          );
        }
      );
  }

  private filterItems(
    value?: string
  ): void {
    const search =
      (value || '')
        .trim()
        .toLowerCase();

    if (!search) {
      this.filteredItems = [
        ...this.items
      ];

      return;
    }

    this.filteredItems =
      this.items.filter((CommissionTableListItem: CommissionTableListItem) => {

          const shortName = (CommissionTableListItem.shortName || '').toLowerCase();

          const name = (CommissionTableListItem.name || '').toLowerCase();

          return (
            shortName.includes(search)
            || name.includes(search)
            || `${shortName} ${name}`
              .includes(search)
          );
        }
      );
  }

  onSelectionChange(
    commissionTableUuid: string
  ): void {
    const commissionTable =
      this.allItems.find(
        (
          item:
          CommissionTableListItem
        ) =>
          item.uuid === commissionTableUuid
      );

    if (commissionTable) {
      this.selectedCommissionTable.emit(
        commissionTable
      );
    }

    this.value =
      commissionTableUuid;

    this.onChange(
      commissionTableUuid
    );
  }

  clearValue(): void {
    this.control.setValue(null);

    this.value = null;

    this.onChange(null);
  }

  writeValue(
    value: string
  ): void {
    if (this.externalControl) {
      return;
    }

    this.value = value;

    this.control.setValue(
      value,
      {
        emitEvent: false
      }
    );
  }

  registerOnChange(
    fn: any
  ): void {
    this.onChange = fn;
  }

  registerOnTouched(
    fn: any
  ): void {
    this.onTouched = fn;
  }

  setDisabledState(
    isDisabled: boolean
  ): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable({
        emitEvent: false
      });
    } else {
      this.control.enable({
        emitEvent: false
      });
    }
  }

  onChange: any = () => {
  };

  onTouched: any = () => {
  };
}
